import { useEffect, useMemo, useRef } from 'react';
import { FieldValues, useForm } from 'react-hook-form';

import { useCleanSearchParams } from './use-clean-searchparams';

type UseSearchboxOptions = {
  isDebounce?: boolean;
  resetPage?: boolean;
};

export function useSearchbox(
  options: UseSearchboxOptions = { isDebounce: true, resetPage: true }
) {
  const { searchParams, setParams } = useCleanSearchParams();
  const params = useMemo(() => {
    const rawParams = {
      query: '',
      ...Object.fromEntries(searchParams.entries()),
    };
    return {
      query: rawParams.query,
    };
  }, [searchParams]);

  const form = useForm({
    defaultValues: {
      search_query: params.query,
    } as FieldValues,
  });

  // Debounce search - enabled for pages that need auto-search
  const timeout = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (options.isDebounce) {
      const subscription = form.watch((value, { name }) => {
        if (name === 'search_query') {
          clearTimeout(timeout.current);
          timeout.current = setTimeout(() => {
            const params: Record<string, string | number> = {
              query: value[name],
            };
            if (options.resetPage) {
              params.page = 1;
            }
            setParams(params);
          }, 500);
        }
      });

      return () => {
        clearTimeout(timeout.current);
        subscription.unsubscribe();
      };
    }
  }, [setParams, form, options.isDebounce, options.resetPage]);

  const handleSearch = () => {
    const searchQuery = form.getValues('search_query');
    const params: Record<string, string | number> = { query: searchQuery };
    if (options.resetPage) {
      params.page = 1;
    }
    setParams(params);
  };

  return { ...form, params, handleSearch };
}
